import React from 'react';
import { useNavigate } from 'react-router-dom';
import BookingStatusBadge from './BookingStatusBadge';
import type { ClientBooking } from '../../../types';

interface CompactBookingCardProps {
  booking: ClientBooking;
}

const CompactBookingCard: React.FC<CompactBookingCardProps> = ({ booking }) => {
  const navigate = useNavigate();

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleClick = () => {
    navigate(`/u/bookings/${booking.id}`);
  };

  return (
    <button
      onClick={handleClick}
      className="w-full bg-white rounded-xl border border-gray-100 p-3.5 hover:border-purple-200 hover:shadow-md transition-all duration-200 active:scale-[0.98] text-left group cursor-pointer"
    >
      <div className="flex items-center gap-3">
        {/* Expert Avatar */}
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-sm">
            {getInitials(booking.expert.display_name)}
          </div>
        </div>

        {/* Booking Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-semibold text-gray-900 truncate">
              {booking.expert.display_name}
            </h4>
            <BookingStatusBadge status={booking.status} size="small" pendingOnly />
          </div>

          <p className="text-xs font-medium text-purple-600 truncate mb-1">
            {booking.service?.name || 'Service N/A'}
          </p>

          <div className="flex items-center text-xs text-gray-500">
            <svg
              className="w-3.5 h-3.5 mr-1.5 text-gray-400"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="truncate">{booking.date_formatted} at {booking.time_formatted}</span>
          </div>
        </div>

        {/* Arrow Icon */}
        <div className="flex-shrink-0 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all">
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </button>
  );
};

export default CompactBookingCard;
